import { useEffect } from 'react'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { useLocation } from 'react-router-dom'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { Link } from 'react-router-dom'

// sessionStorage key guarding the one-time chunk-error reload (cleared on a
// successful mount in main.tsx, so a later unrelated chunk error can reload again).
export const CHUNK_RELOAD_KEY = 'chunk-reload-once'

// A content-hashed lazy chunk 404s for any tab held open across a deploy (output
// is `clean: true`, so old hashes vanish). React.lazy memoizes the rejected
// import() promise, so the "Retry this page" button just re-throws the cached
// rejection forever — only re-fetching index.html (with the new chunk names)
// recovers. Detect that class of error so we can do a guarded one-time reload.
export function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  if (err.name === 'ChunkLoadError') return true
  return /Loading chunk [\w-]+ failed|Loading CSS chunk|dynamically imported module/i.test(err.message)
}

function Fallback({ error, resetErrorBoundary }: FallbackProps) {
  // Reset the boundary when the user navigates — otherwise the error
  // state sticks even after they click another sidebar link.
  const location = useLocation()
  useEffect(() => { resetErrorBoundary() }, [location.pathname, resetErrorBoundary])

  const message = error instanceof Error ? error.message : String(error)
  return (
    <div className="grid min-h-[50vh] place-items-center p-6">
      <div className="penser-card max-w-lg p-6 text-center">
        <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-red-50 text-red-600">
          <AlertTriangle size={18} />
        </div>
        <div className="text-sm font-semibold text-slate-800">This page hit an unexpected error.</div>
        <div className="mt-1 text-xs text-slate-500">
          You can recover without losing your other tabs — just retry or go back to the dashboard.
        </div>
        <pre className="mt-3 max-h-40 overflow-auto rounded-md bg-slate-50 px-3 py-2 text-left font-mono text-[11px] text-slate-700">
          {message}
        </pre>
        <div className="mt-4 flex justify-center gap-2">
          <button className="penser-button-ghost" onClick={resetErrorBoundary}>
            <RefreshCw size={12} /> Retry this page
          </button>
          <Link to="/" className="penser-button-primary" onClick={resetErrorBoundary}>
            <Home size={12} /> Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  )
}

// Clears the one-shot chunk-reload flag once the route subtree renders cleanly.
// If any child throws during render, React aborts that commit and this effect
// never runs, so the flag only clears on a genuinely successful mount — letting
// a later, unrelated stale-chunk error trigger a fresh recovery reload.
function ChunkReloadFlagResetter() {
  useEffect(() => {
    try { sessionStorage.removeItem(CHUNK_RELOAD_KEY) } catch { /* no-op */ }
  }, [])
  return null
}

export function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      FallbackComponent={Fallback}
      onError={(err) => {
        // A stale lazy chunk (old hash 404s after a deploy) cannot be recovered
        // by the Retry button — React.lazy caches the rejected import promise.
        // Re-fetch index.html once (new content-hashed chunk names) to recover.
        // The sessionStorage flag prevents an infinite reload loop if the chunk
        // is genuinely gone; it's cleared on the next successful mount.
        if (isChunkLoadError(err) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
          window.location.reload()
          return
        }
        // CloudWatch via stdout in dev + structured error in prod telemetry
        // (Sentry-style hookup can plug in here later).
        console.error('[route error]', err)
      }}
    >
      <ChunkReloadFlagResetter />
      {children}
    </ErrorBoundary>
  )
}
